// Our base component, which all others extend.
import { OutlineElement } from '../../base/outline-element/outline-element';
import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import componentStyles from './sic-grid.css.lit';

/**
 * The sic grid component
 * @element sic-grid
 *
 * @slot - The default, and only slot for this element.
 *
 * @attr row-gap
 * @attr col-gap
 * @attr columns
 * @attr item-min-width
 * @attr align-items
 * @attr justify-items
 * @attr equal-height
 */
@customElement('sic-grid')
export class SicGrid extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * Vertical gap between items.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'row-gap',
  })
  rowGap = '2rem';

  /**
   * Horizontal gap between items.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'col-gap',
  })
  colGap = '2rem';

  /**
   * Maximum amount of columns.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'columns',
  })
  columns = '3';

  /**
   * Minimum width of each item.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'item-min-width',
  })
  itemMinWidth = '200px';

  /**
   * Vertical alignment of each item.
   */
  @property({
    type: String,
    reflect: false,
    attribute: 'align-items',
  })
  alignItems = '';

  /**
   * Horizontal alignment of each item.
   */
  @property({
    type: String,
    reflect: false,
    attribute: 'justify-items',
  })
  justifyItems = '';

  /**
   * Whether or not all rows will have equal height.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'equal-height',
  })
  equalHeight = false;

  updated() {
    // Set css vars from properties.
    this.setVars();
  }
  /**
   * Set css vars without injecting an inline <style>.
   */
  setVars() {
    this.style.setProperty('--grid-layout-column-gap', `${this.colGap}`);
    this.style.setProperty('--grid-layout-row-gap', `${this.rowGap}`);
    this.style.setProperty('--grid-column--max-count', `${this.columns}`);
    this.style.setProperty('--grid-item--min-width', `${this.itemMinWidth}`);
    this.style.setProperty('--align-items', `${this.alignItems || 'auto'}`);
    this.style.setProperty('--justify-items', `${this.justifyItems || 'auto'}`);
  }

  render(): TemplateResult {
    const wrapperClasses = {
      'grid-wrapper': true,
      'align-items': this.alignItems || '',
      'justify-items': this.justifyItems || '',
      'equal-height': this.equalHeight || '',
    };
    return html`<div class="${classMap(wrapperClasses)}">
      <slot></slot>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-grid': SicGrid;
  }
}
